import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { storage, STORAGE_KEYS } from '@utils/storage';
import type { ApiError, ApiResponse, AuthTokens, User } from '@/types';

type FailedQueueItem = {
  requestConfig: InternalAxiosRequestConfig & { _retry?: boolean };
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
};

type RefreshState = {
  isRefreshing: boolean;
  failedQueue: FailedQueueItem[];
};

const API_BASE = import.meta.env.VITE_API_URL || '/api';

function getRefreshState(): RefreshState {
  const g = globalThis as typeof globalThis & { __lyt_refresh_state__?: RefreshState };
  if (!g.__lyt_refresh_state__) {
    g.__lyt_refresh_state__ = { isRefreshing: false, failedQueue: [] };
  }
  return g.__lyt_refresh_state__;
}

function hasSession(): boolean {
  const tokens = storage.get<AuthTokens>(STORAGE_KEYS.AUTH_TOKENS);
  const user = storage.get<User>(STORAGE_KEYS.USER_DATA);
  return Boolean(tokens?.access_token && user);
}

const PUBLIC_PATHS = ['/', '/login', '/auth/sso/callback'];

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE,
      timeout: 30000,
      headers: { 'Content-Type': 'application/json' },
    });
    this.setupInterceptors();
  }

  private persistTokens(tokens: AuthTokens) {
    storage.set(STORAGE_KEYS.AUTH_TOKENS, tokens);
    this.client.defaults.headers.common.Authorization = `Bearer ${tokens.access_token}`;
    window.dispatchEvent(new CustomEvent('auth:tokens', { detail: { tokens } }));
  }

  private clearSession() {
    storage.remove(STORAGE_KEYS.AUTH_TOKENS);
    storage.remove(STORAGE_KEYS.USER_DATA);
    delete this.client.defaults.headers.common.Authorization;
    window.dispatchEvent(new CustomEvent('auth:clear'));
  }

  private processQueue(error: unknown, tokens: AuthTokens | null) {
    const state = getRefreshState();
    const queue = state.failedQueue;
    state.failedQueue = [];
    for (const item of queue) {
      if (error || !tokens) {
        item.reject(error);
        continue;
      }
      item.requestConfig.headers = item.requestConfig.headers ?? {};
      item.requestConfig.headers.Authorization = `Bearer ${tokens.access_token}`;
      item.resolve(this.client(item.requestConfig));
    }
  }

  private setupInterceptors() {
    this.client.interceptors.request.use((config) => {
      config.headers = config.headers ?? {};
      const tokens = storage.get<AuthTokens>(STORAGE_KEYS.AUTH_TOKENS);
      if (hasSession() && tokens?.access_token) {
        config.headers.Authorization = `Bearer ${tokens.access_token}`;
      }
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError<ApiError>) => {
        const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
        const url = original?.url ?? '';

        // Subscription required (trial expired) → send the user to the paywall.
        if (error.response?.status === 402) {
          window.dispatchEvent(new CustomEvent('subscription:required'));
          const onRestaurantPage = window.location.pathname.startsWith('/restaurant');
          const params = new URLSearchParams(window.location.search);
          if (onRestaurantPage && params.get('tab') !== 'subscription') {
            window.location.href = '/restaurant?tab=subscription';
          }
          return Promise.reject(error);
        }

        if (url.includes('/auth/') || error.response?.status !== 401 || original._retry) {
          return Promise.reject(error);
        }

        const tokens = storage.get<AuthTokens>(STORAGE_KEYS.AUTH_TOKENS);
        if (!tokens?.refresh_token) {
          this.clearSession();
          if (!PUBLIC_PATHS.includes(window.location.pathname)) {
            window.location.href = '/login';
          }
          return Promise.reject(error);
        }

        const state = getRefreshState();
        original._retry = true;

        if (state.isRefreshing) {
          return new Promise((resolve, reject) => {
            state.failedQueue.push({ requestConfig: original, resolve, reject });
          });
        }

        state.isRefreshing = true;
        try {
          const res = await axios.post<ApiResponse<AuthTokens>>(`${API_BASE}/auth/refresh`, {
            refresh_token: tokens.refresh_token,
          });
          const newTokens = res.data.data;
          this.persistTokens(newTokens);
          this.processQueue(null, newTokens);
          original.headers = original.headers ?? {};
          original.headers.Authorization = `Bearer ${newTokens.access_token}`;
          return this.client(original);
        } catch (refreshError) {
          this.processQueue(refreshError, null);
          this.clearSession();
          if (!PUBLIC_PATHS.includes(window.location.pathname)) {
            window.location.href = '/login';
          }
          return Promise.reject(refreshError);
        } finally {
          state.isRefreshing = false;
        }
      },
    );
  }

  get<T>(url: string, config = {}) {
    return this.client.get<ApiResponse<T>>(url, config);
  }

  post<T>(url: string, data?: unknown, config = {}) {
    return this.client.post<ApiResponse<T>>(url, data, config);
  }

  put<T>(url: string, data?: unknown, config = {}) {
    return this.client.put<ApiResponse<T>>(url, data, config);
  }

  patch<T>(url: string, data?: unknown, config = {}) {
    return this.client.patch<ApiResponse<T>>(url, data, config);
  }

  delete<T>(url: string, config = {}) {
    return this.client.delete<ApiResponse<T>>(url, config);
  }
}

export const apiClient = new ApiClient();

export function unwrap<T>(response: { data: ApiResponse<T> }): T {
  return response.data.data;
}

export function getErrorMessage(error: unknown, fallback = 'Bir hata oluştu'): string {
  if (axios.isAxiosError<ApiError>(error)) {
    return error.response?.data?.error?.message ?? error.message ?? fallback;
  }
  if (error instanceof Error) return error.message;
  return fallback;
}
